import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Category from '@mui/icons-material/Category';
import CloseIcon from '@mui/icons-material/Close';
import { skipToken } from '@reduxjs/toolkit/query';

import {
  useGetBankTransactionsQuery,
  useUpdateTransactionMutation,
  BankTransaction,
} from '@/store/transactionsApi';
// import { useGetCashflowCategoriesQuery } from '@/store/cashFlowApi';
import CategorySelector from '@/modules/cashFlow/components/CategorySelector';
import { formatNumberToCurrency } from '@/utils/currency';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { formatAppDate, parseOptionalDate } from '@/utils/date';
import { useLocalization } from '@/hooks/useLocalization';

interface UncategorizedTransactionWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  startDate: string;
  endDate: string;
  investorId?: number;
  onRefreshStart: () => void;
  isChaining?: boolean;
  chainProgress?: { current: number; total: number };
}

interface TransactionStep {
  transaction: BankTransaction;
  categoryId?: number;
  alwaysChange?: boolean;
  skipped?: boolean;
}

interface EditedTransactionStep extends TransactionStep {
  categoryId: number;
}

const isEditedStep = (step: TransactionStep): step is EditedTransactionStep =>
  Boolean(step.categoryId) && !step.skipped;

const UncategorizedTransactionWizard: React.FC<
  UncategorizedTransactionWizardProps
> = ({
  open,
  onClose,
  onComplete,
  startDate,
  endDate,
  investorId,
  onRefreshStart,
  isChaining = false,
  chainProgress,
}) => {
  const { t } = useLocalization();
  const authLoaded = useAuthIsLoaded();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [currentIndex, setCurrentIndex] = useState(0);
  const [steps, setSteps] = useState<TransactionStep[]>([]);
  //const [currentStep, setCurrentStep] = useState<'category' | 'rule'>('category');
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    number | undefined
  >();
  const [isSaving, setIsSaving] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  //const [bulkUpdateTransactions] = useBulkUpdateTransactionsMutation();

  // Queries
  const { data: transactionsData, isLoading: transactionsLoading } =
    useGetBankTransactionsQuery(
      authLoaded
        ? {
            startDate,
            endDate,
            OnlyUncategorized: true,
            hideTransfers: true,
            sort: { Property: 'TransactionDate', Direction: 'asc' },
            limit: 500,
            ...(typeof investorId === 'number'
              ? { InvestorId: investorId }
              : {}),
          }
        : skipToken,
    );

  // const { data: categoriesData } = useGetCashflowCategoriesQuery(
  //   authLoaded && typeof investorId === 'number' ? { InvestorId: investorId } : skipToken
  // );

  const [updateTransaction] = useUpdateTransactionMutation();

  // Init steps on load
  useEffect(() => {
    if (transactionsData?.transactions) {
      const newSteps = transactionsData.transactions.map((transaction) => ({
        transaction,
      }));
      setSteps(newSteps);
      setCurrentIndex(0);
      //setCurrentStep('category');
      setSelectedCategoryId(undefined);
      setIsComplete(false); // IMPORTANT: reset complete state on new data
    }
  }, [transactionsData]);

  const currentTransaction: BankTransaction | undefined =
    currentIndex >= 0 && currentIndex < steps.length
      ? steps[currentIndex].transaction
      : undefined;

  const resolvedTransactions = useMemo(() => {
    return steps.reduce(
      (count, step) => (step.categoryId && !step.skipped ? count + 1 : count),
      0,
    );
  }, [steps]);

  const skippedCount = useMemo(
    () => steps.filter((s) => s.skipped).length,
    [steps],
  );

  const editedCount = useMemo(
    () => steps.filter((s) => !!s.categoryId && !s.skipped).length,
    [steps],
  );

  const editedSteps = useMemo(
    () => steps.filter((s) => !!s.categoryId && !s.skipped),
    [steps],
  );

  // Helpers
  const wipeMany = (indices: number[]) => {
    setSteps((prevSteps) => {
      const nextSteps = [...prevSteps];
      for (const idx of indices) {
        if (idx < 0 || idx >= nextSteps.length) continue;
        nextSteps[idx] = {
          ...nextSteps[idx],
          categoryId: undefined,
          alwaysChange: undefined,
          skipped: undefined,
        };
      }
      return nextSteps;
    });
  };

  // Actions
  const handleCategorySelect = (categoryId: number) => {
    if (!currentTransaction) return;

    setSteps((prev) => {
      const next = [...prev];
      next[currentIndex] = {
        ...next[currentIndex],
        categoryId,
        skipped: undefined,
        alwaysChange: undefined,
      };
      return next;
    });

    setSelectedCategoryId(categoryId);
    //setCurrentStep('rule');
  };

  const moveToNext = () => {
    setSelectedCategoryId(undefined);
    //setCurrentStep('category');

    if (currentIndex < steps.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      setIsComplete(true);
    }
  };

  // const handleAlwaysChange = (alwaysChange: boolean) => {
  //   if (!currentTransaction || selectedCategoryId === undefined) return;

  //   setSteps((prev) => {
  //     const next = [...prev];
  //     next[currentIndex] = {
  //       ...next[currentIndex],
  //       categoryId: selectedCategoryId,
  //       alwaysChange,
  //     };
  //     return next;
  //   });

  //   moveToNext();
  // };

  const handleSkip = () => {
    setSteps((prev) => {
      const next = [...prev];
      next[currentIndex] = {
        ...next[currentIndex],
        skipped: true,
        categoryId: undefined,
        alwaysChange: undefined,
      };
      return next;
    });

    moveToNext();
  };

  const moveBack = () => {
    if (isComplete) {
      setIsComplete(false);
      const lastIdx = steps.length - 1;
      wipeMany([lastIdx]);
      setCurrentIndex(lastIdx);
      setSelectedCategoryId(undefined);
      // setCurrentIndex((i) => i - 1);
      // setCurrentStep('category');
      return;
    }

    // if (currentStep === 'rule') {
    //   // If you go back from rule -> category for same item, wipe its selection so user must re-select
    //   wipeMany([currentIndex]);
    //   setSelectedCategoryId(undefined);
    //   setCurrentStep('category');
    //   return;
    // }

    if (currentIndex > 0) {
      // Wipe current and previous to make “back then forward” explicit
      wipeMany([currentIndex, currentIndex - 1]);
      setSelectedCategoryId(undefined);
      setCurrentIndex((i) => i - 1);
      // setCurrentStep('category');
    }
  };

  const handleFinish = async () => {
    setIsSaving(true);
    try {
      const transactionsToUpdate = steps.filter(isEditedStep);

      // Only update transactions if there are any to update
      if (transactionsToUpdate.length > 0) {
        for (const step of transactionsToUpdate) {
          const tx = step.transaction;

          // Build a "full" payload from the existing transaction,
          // then override only the fields you intend to change.
          const transactionDate = parseOptionalDate(tx.TransactionDate);
          const payload: BankTransaction = {
            ...tx,

            // Always include the id (some APIs require it in body)
            WealthAccountTransactionId: tx.WealthAccountTransactionId,

            // Override the fields being edited
            BankTransactionCategoryId: step.categoryId,
            // AlwaysChange: !!step.alwaysChange,
            AlwaysChange: false,

            // Only include ComparisonOperator when AlwaysChange is on
            // ...(step.alwaysChange ? { ComparisonOperator: 1 } : {}),

            // Ensure TransactionDate is sent and in a safe format
            ...(transactionDate
              ? { TransactionDate: transactionDate.toISOString() }
              : {}),
          };

          console.log('updating transaction ===>', payload);

          await updateTransaction({
            transactionId: tx.WealthAccountTransactionId,
            transaction: payload,
          }).unwrap();
        }
        //console.log("invalidate the tag =====> WizardEligibility");
        onRefreshStart();
      }

      // Call onComplete if available (for chaining), otherwise onClose
      if (onComplete) {
        onComplete();
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Error updating transactions:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkipWizard = () => {
    // Skip the entire wizard without saving anything
    if (onComplete) {
      onComplete();
    } else {
      onClose();
    }
  };

  // const handleFinish = async () => {
  //   setIsSaving(true);

  //   try {
  //     const transactionsToUpdate: Partial<BankTransaction>[] = steps
  //       .filter(s => s.categoryId && !s.skipped)
  //       .map(s => ({
  //         WealthAccountTransactionId: s.transaction.WealthAccountTransactionId, // required in body
  //         BankTransactionCategoryId: s.categoryId!,
  //         AlwaysChange: !!s.alwaysChange,
  //         ComparisonOperator: s.alwaysChange ? 1 : undefined,
  //       }));

  //     if (transactionsToUpdate.length === 0) {
  //       onClose();
  //       return;
  //     }

  //     await bulkUpdateTransactions({ transactions: transactionsToUpdate }).unwrap();
  //     onClose();
  //   } catch (error) {
  //     console.error("Bulk update failed:", error);
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  if (!open) return null;

  // One dialog, conditional content.
  // Early return only for “no transactions” (still using the same single-dialog pattern).
  const hasTransactions = !!transactionsData?.transactions.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='start'
          gap={1}
        >
          <Box>
            <Typography variant='h6'>
              {t(
                'UncategorizedWizard.uncategorized.title',
                'Uncategorized Transaction Wizard',
              )}
            </Typography>

            {/* Chain progress indicator */}
            {/*isChaining && chainProgress && (
              <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                {t('UncategorizedWizard.chainProgress', 'Step {{current}} of {{total}}', {
                  current: chainProgress.current,
                  total: chainProgress.total,
                })}
              </Typography>
            )*/}

            {/* Subtitle varies based on state */}
            {hasTransactions && !transactionsLoading && (
              <Typography
                variant='body2'
                color='text.secondary'
              >
                {isComplete
                  ? t(
                      'UncategorizedWizard.uncategorized.completeSubtitle',
                      'All transactions reviewed ({{total}})',
                      {
                        total: steps.length,
                      },
                    )
                  : t(
                      'UncategorizedWizard.uncategorized.subtitle',
                      'Transaction {{current}} of {{total}}',
                      {
                        current: currentIndex + 1,
                        total: steps.length,
                      },
                    )}
              </Typography>
            )}
          </Box>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Loading state */}
        {transactionsLoading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography
              variant='h6'
              gutterBottom
            >
              {t('UncategorizedWizard.loading', 'Loading transactions...')}
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {/* No transactions */}
        {!transactionsLoading && !hasTransactions && (
          <Alert severity='info'>
            {t(
              'UncategorizedWizard.uncategorized.noTransactions',
              'No uncategorized transactions found for the selected date range.',
            )}
          </Alert>
        )}

        {/* Saving overlay-ish content (still inside same Dialog) */}
        {!transactionsLoading && hasTransactions && isSaving && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography
              variant='h6'
              gutterBottom
            >
              {t('UncategorizedWizard.saving', 'Saving changes...')}
            </Typography>
            <LinearProgress />
            <Typography
              variant='body2'
              sx={{ mt: 2 }}
            >
              {t(
                'UncategorizedWizard.savingProgress',
                'Updating {{count}} transactions',
                {
                  count: steps.filter((s) => s.categoryId && !s.skipped).length,
                },
              )}
            </Typography>
          </Box>
        )}

        {/* Main content */}
        {!transactionsLoading && hasTransactions && !isSaving && (
          <>
            {/* Description alert changes based on completion */}
            <Alert
              severity='info'
              sx={{ mb: 3 }}
            >
              {isComplete
                ? t(
                    'UncategorizedWizard.uncategorized.allDone',
                    'All transactions are reviewed.',
                  )
                : t(
                    'UncategorizedWizard.uncategorized.description',
                    'Help us categorize your transactions to provide better insights into your spending patterns.',
                  )}
            </Alert>

            {/* Complete screen content */}
            {isComplete ? (
              <Box>
                <Typography
                  variant='subtitle1'
                  gutterBottom
                >
                  {t('UncategorizedWizard.summaryTitle', 'Summary')}
                </Typography>

                {/* Counts */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 2,
                      flex: { xs: 1, md: 0 },
                      minWidth: { xs: '100%', sm: '320px' },
                    }}
                  >
                    <Typography
                      variant='caption'
                      color='text.secondary'
                    >
                      {t(
                        'UncategorizedWizard.summary.edited',
                        'Will be updated',
                      )}
                    </Typography>
                    <Typography variant='h6'>{editedCount}</Typography>
                  </Paper>

                  <Paper
                    variant='outlined'
                    sx={{
                      p: 2,
                      flex: { xs: 1, md: 0 },
                      minWidth: { xs: '100%', sm: '320px' },
                    }}
                  >
                    <Typography
                      variant='caption'
                      color='text.secondary'
                    >
                      {t('UncategorizedWizard.summary.skipped', 'Skipped')}
                    </Typography>
                    <Typography variant='h6'>{skippedCount}</Typography>
                  </Paper>
                </Box>

                {/* Edited preview */}
                <Typography
                  variant='subtitle1'
                  sx={{ mb: 1 }}
                >
                  {t(
                    'UncategorizedWizard.summary.updatedTransactions',
                    'Updated Transactions',
                  )}
                </Typography>

                {editedSteps.length === 0 ? (
                  <Typography variant='body2'>
                    {t(
                      'UncategorizedWizard.summary.noEdits',
                      'No transactions will be updated.',
                    )}
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      maxHeight: 280,
                      overflow: 'auto',
                      pr: 1,
                    }}
                  >
                    {editedSteps.map((s) => {
                      const tx = s.transaction;

                      return (
                        <Paper
                          key={tx.WealthAccountTransactionId}
                          variant='outlined'
                          sx={{ p: 1.5 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 2,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant='body2'
                                fontWeight={600}
                                noWrap
                              >
                                {tx.DisplayDescription}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                              >
                                {formatAppDate(tx.TransactionDate, {
                                  format: 'MMM DD, YYYY',
                                })}{' '}
                                • {tx.Name}
                              </Typography>
                            </Box>

                            <Box sx={{ textAlign: 'right' }}>
                              <Typography
                                variant='body2'
                                fontWeight={600}
                              >
                                {formatNumberToCurrency(tx.Amount)}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </Box>
            ) : (
              <>
                {/* Transaction details */}
                {currentTransaction && (
                  <>
                    <Paper
                      elevation={1}
                      sx={{ p: 2, mb: 3 }}
                    >
                      <Typography
                        variant='subtitle1'
                        gutterBottom
                      >
                        {t(
                          'UncategorizedWizard.transactionDetails',
                          'Transaction Details',
                        )}
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                          >
                            {t(
                              'UncategorizedWizard.description',
                              'Description',
                            )}
                          </Typography>
                          <Typography
                            variant='body1'
                            fontWeight='medium'
                          >
                            {currentTransaction.DisplayDescription}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                          >
                            {t('UncategorizedWizard.amount', 'Amount')}
                          </Typography>
                          <Typography
                            variant='body1'
                            fontWeight='medium'
                          >
                            {formatNumberToCurrency(currentTransaction.Amount)}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                          >
                            {t('UncategorizedWizard.date', 'Date')}
                          </Typography>
                          <Typography variant='body1'>
                            {formatAppDate(currentTransaction.TransactionDate, {
                              format: 'MMM DD, YYYY',
                            })}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                          >
                            {t('UncategorizedWizard.account', 'Account')}
                          </Typography>
                          <Typography variant='body1'>
                            {currentTransaction.Name}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {/* Category step */}
                    {
                      /*currentStep === 'category' && (*/
                      <Box>
                        <Typography
                          variant='h6'
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Category />
                          {t(
                            'UncategorizedWizard.selectCategory',
                            'Select Category',
                          )}
                        </Typography>

                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ mb: 2 }}
                        >
                          {t(
                            'UncategorizedWizard.selectCategoryHint',
                            'Choose the most appropriate category for this transaction.',
                          )}
                        </Typography>

                        <CategorySelector
                          label={t(
                            'UncategorizedWizard.selectCategory',
                            'Select Category',
                          )}
                          value={selectedCategoryId ?? ''}
                          onChange={(id: number | '') => {
                            if (typeof id === 'number')
                              handleCategorySelect(id);
                          }}
                          investorId={investorId}
                        />
                      </Box>
                      /*)*/
                    }

                    {/* Rule step */}
                    {/*currentStep === 'rule' && (
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {t('UncategorizedWizard.createRule', 'Create Rule')}
                        </Typography>

                        <Typography variant="body1" gutterBottom>
                          {t(
                            'UncategorizedWizard.alwaysChangeQuestion',
                            'Always categorize transactions with description "{{description}}" as {{category}}?',
                            {
                              description: currentTransaction.DisplayDescription,
                              category:
                                categoriesData?.categories?.find(
                                  (c) => c.BankTransactionCategoryId === selectedCategoryId
                                )?.Name || 'this category',
                            }
                          )}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {t('UncategorizedWizard.ruleHint', 'This will automatically categorize similar transactions in the future.')}
                        </Typography>
                      </Box>
                    )*/}
                  </>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {/* If loading / no transactions / saving, you can keep actions minimal */}
        {transactionsLoading || !hasTransactions || isSaving ? (
          <Button onClick={onClose}>
            {t('UncategorizedWizard.close', 'Close')}
          </Button>
        ) : (
          <>
            {/* Back */}
            {/* {(currentIndex > 0 || currentStep === 'rule' || isComplete) && ( */}
            {(currentIndex > 0 || isComplete) && (
              <Button
                variant='outlined'
                startIcon={<ArrowBack />}
                onClick={moveBack}
                disabled={isSaving}
              >
                {t('UncategorizedWizard.back', 'Back')}
              </Button>
            )}

            {/* Skip (only while reviewing and on category step) */}
            {/* {!isComplete && currentStep === 'category' && ( */}
            {!isComplete && (
              <Button
                variant='outlined'
                onClick={handleSkip}
                disabled={isSaving}
              >
                {t('UncategorizedWizard.skip', 'Skip')}
              </Button>
            )}

            {/* Yes/No (rule step) */}
            {/* {!isComplete && currentStep === 'rule' && (
              <>
                <Button variant="contained" onClick={() => handleAlwaysChange(true)} disabled={isSaving}>
                  {t('UncategorizedWizard.yes', 'Yes')}
                </Button>
                <Button variant="outlined" onClick={() => handleAlwaysChange(false)} disabled={isSaving}>
                  {t('UncategorizedWizard.no', 'No')}
                </Button>
              </>
            )} */}
            {!isComplete && currentTransaction && (
              <Button
                variant='contained'
                onClick={moveToNext}
                disabled={!steps[currentIndex]?.categoryId || isSaving}
              >
                {currentIndex === steps.length - 1
                  ? t('UncategorizedWizard.review', 'Review')
                  : t('UncategorizedWizard.next', 'Next')}
              </Button>
            )}

            {/* Finish/Skip — this branch already requires hasTransactions */}
            {/* {(isComplete || (hasTransactions && currentIndex >= steps.length)) && ( */}
            <Button
              variant='contained'
              onClick={
                resolvedTransactions > 0 ? handleFinish : handleSkipWizard
              }
              disabled={isSaving}
            >
              {resolvedTransactions > 0
                ? // Has changes to save
                  isChaining &&
                  chainProgress &&
                  chainProgress.current < chainProgress.total
                  ? t('UncategorizedWizard.finishAndNext', 'Finish & Next')
                  : t('UncategorizedWizard.finish', 'Finish')
                : // No changes to save - skip option
                  isChaining &&
                    chainProgress &&
                    chainProgress.current < chainProgress.total
                  ? t('UncategorizedWizard.skipAndNext', 'Close & Next')
                  : t('UncategorizedWizard.close', 'Close')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UncategorizedTransactionWizard;
